import { useCallback, useEffect, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { holdIdleExit } from '../../services/idleHold';
import { aendereEmpfaenger, fetchEmpfaenger } from '../../services/planungsphaseService';
import { ADRESSAT_LABEL } from './fragenkatalog';
import {
    darfSchreiben,
    erzeugeEmpfaengerSchreiber,
    leereLage,
    schliesseKonflikt,
    uebernehmeAenderung,
    uebernehmeBestaetigung,
    uebernehmeLesung,
    type EmpfaengerEintrag,
    type EmpfaengerLage,
    type EmpfaengerSpeicherstand,
} from './empfaenger';
import type { FrageAdressat } from './types';

/**
 * Zustand und Speichern der Zuordnung „Adressat → Person" (Spalte
 * `planungsphasen.empfaenger`, Nr. 162).
 *
 * Eigener Stand neben `phase`, aus demselben Grund wie bei der
 * Unterlagen-Checkliste (`useUnterlagen.ts`, Kopf): `PlanungModul` setzt den
 * Entwurf der Grundlagen bei jeder Aenderung von `phase` neu auf — die
 * Zuordnung darf `phase` deshalb nie anfassen. Gelesen wird die Spalte
 * einzeln (`fetchEmpfaenger`); geschrieben wird JE ADRESSAT als
 * Lese-Aendern-Schreiben mit Sperre auf `updated_at` (`aendereEmpfaenger`) —
 * zwei Sitzungen ueberschreiben sich so nicht mehr gegenseitig. Die
 * Reihenfolge und Wiederholung der Einzelaenderungen haelt der Schreiber
 * (`erzeugeEmpfaengerSchreiber`).
 *
 * Die UEBERGAENGE des angezeigten Standes sind reine Funktionen in
 * `empfaenger.ts` (`uebernehmeLesung`, `uebernehmeBestaetigung`,
 * `uebernehmeAenderung`, `darfSchreiben`) und dort getestet; dieser Hook
 * verdrahtet sie nur:
 *   - nach jedem bestaetigten Schreiben gilt der SERVERSTAND (mit den
 *     Aenderungen anderer) plus das, was hier noch aussteht;
 *   - die Leseantwort des Phasenwechsels gilt nur, solange seither nichts
 *     bestaetigt wurde — der bestaetigte Stand gewinnt (`bestaetigungen`);
 *   - meldet der Service `konflikt`, zeigt die Lage den fremden Wert und
 *     eine Meldung, bis der Mensch sie schliesst.
 * Die Rueckrufe des Schreibers arbeiten nur mit funktionalen
 * Zustandsaenderungen — ohne Refs, ohne veraenderliche Werte im Abschluss.
 *
 * JEDER Schreibweg (Setzen, Entfernen, „Erneut speichern") steht unter
 * denselben Riegeln (`darfSchreiben`): Nur-Ansicht, keine Phase, Stand laedt,
 * `hinweis` (Spalte/Tabelle fehlt oder Lesen gescheitert).
 */
export const useEmpfaengerZuordnung = (planungId: string | null, gesperrt: boolean) => {
    const [lage, setLage] = useState<EmpfaengerLage>(() => leereLage(planungId));
    const [laden, setLaden] = useState(!!planungId);
    /** Spalte/Tabelle fehlt oder Lesen gescheitert — dann nur anzeigen, nie schreiben. */
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [stand, setStand] = useState<EmpfaengerSpeicherstand>('gespeichert');
    const [fehler, setFehler] = useState<string | null>(null);
    // Ein Schreiber je Instanz — `useState` mit Initialisierer, damit er beim
    // Rendern nicht jedes Mal neu entsteht. Seine Rueckrufe fassen nur die
    // (stabilen) Setzer an.
    const [schreiber] = useState(() => erzeugeEmpfaengerSchreiber({
        // Die Basis eines frueheren Versuchs desselben Auftrags geht mit (Codex Runde 3).
        schreibe: a => aendereEmpfaenger(a.phaseId, a.adressat, a.eintrag, a.basis ?? null),
        uebernommen: (aenderung, ergebnis, ausstehend) => {
            setLage(alt => uebernehmeBestaetigung(alt, aenderung, ergebnis, ausstehend));
        },
        melde: (s, f) => {
            setStand(s);
            if (f !== undefined) setFehler(f);
        },
        fehlerText: e => describeError(e),
    }));

    // Phasenwechsel im Rendern verarbeiten statt in einem Effekt (React-
    // Muster „Zustand beim Rendern anpassen", wie `useUnterlagenStand`): der
    // alte Stand ist ab jetzt fremd — leeren und, falls es eine Phase gibt,
    // als ladend markieren, bis die Antwort da ist.
    if (planungId !== lage.phaseId) {
        setLage(leereLage(planungId));
        setHinweis(null);
        setLaden(!!planungId);
    }

    useEffect(() => {
        if (!planungId) return;
        let aktuell = true;
        fetchEmpfaenger(planungId)
            .then(lesung => {
                if (!aktuell) return;
                // Was hier noch aussteht (gescheitert oder unterwegs), liegt
                // auf dem gelesenen Stand; ist seit dem Phasenwechsel schon
                // etwas bestaetigt, bleibt der bestaetigte Stand.
                const ausstehend = schreiber.ausstehendFuer(planungId);
                setLage(alt => uebernehmeLesung(alt, planungId, lesung.zuordnung, ausstehend));
                setHinweis(lesung.hinweis);
                setLaden(false);
            })
            .catch(e => {
                if (!aktuell) return;
                setHinweis(`Die Empfänger konnten nicht gelesen werden: ${describeError(e)}`);
                setLaden(false);
            });
        return () => { aktuell = false; };
    }, [planungId, schreiber]);

    const schreibbar = darfSchreiben({ gesperrt, planungId, laden, hinweis });

    /**
     * Eintrag setzen oder (mit `null`) entfernen. `false`, wenn gerade nicht
     * geschrieben werden darf. Die Anzeige springt sofort um; geschrieben wird
     * je Adressat gegen den frischen Serverstand — der Service laesst eine
     * Aenderung aus, die dort schon so steht, und meldet Konflikt, wenn jemand
     * anderes denselben Adressaten dazwischen geaendert hat.
     */
    const setze = useCallback((adressat: FrageAdressat, eintrag: EmpfaengerEintrag | null): boolean => {
        if (!schreibbar || !planungId) return false;
        setLage(alt => uebernehmeAenderung(alt, planungId, adressat, eintrag));
        schreiber.vormerken({ phaseId: planungId, adressat, eintrag });
        return true;
    }, [schreibbar, planungId, schreiber]);

    /** „Erneut speichern" — unter denselben Riegeln wie `setze`. */
    const erneutSpeichern = useCallback((): boolean => {
        if (!schreibbar) return false;
        void schreiber.speichern();
        return true;
    }, [schreibbar, schreiber]);

    const schliesseMeldung = useCallback(() => {
        setLage(alt => schliesseKonflikt(alt));
    }, []);

    // Der Leerlaufrueckzug darf das Projekt nicht schliessen, solange hier
    // etwas Ungespeichertes liegt — auch nicht nach einem Fehler.
    useEffect(() => {
        if (stand === 'gespeichert') return;
        return holdIdleExit('planungsphase-empfaenger');
    }, [stand]);

    const meldung = lage.konflikt
        ? `${ADRESSAT_LABEL[lage.konflikt]} (${lage.konflikt}): Die Zuordnung wurde inzwischen von jemand anderem geändert — `
          + 'der aktuelle Stand ist übernommen. Bitte prüfen und bei Bedarf erneut setzen.'
        : null;

    return {
        zuordnung: lage.zuordnung, laden, hinweis, meldung, stand, fehler, schreibbar,
        setze, erneutSpeichern, schliesseMeldung,
    };
};
